import { format } from 'date-fns'
import { Box, Card, Divider, Flex, Heading, Text } from '@applyboard/crystal-ui'
import { ReviewRequirementsButton } from './ReviewRequirementsButton'
import { getApplicationFeeAmount, hasApplicationFee } from '../../../utils'
import { StudentApplication } from '../types'
import { AlternateProgramSelected } from '@backend/resources/application/types'

type MobileHeaderProps = Readonly<{
  application: StudentApplication
  showProgramName?: boolean
}>

export function MobileHeader({ application, showProgramName = true }: MobileHeaderProps) {
  const program = application?.attributes?.programSelected
  const alternatePrograms = application?.attributes?.alternateProgramSelected ?? []
  const applicationFeeAmount = getApplicationFeeAmount(application)

  return (
    <Card emphasis="highlighted" aria-label="Application Information">
      <Flex direction="column" gap={4}>
        <Flex direction="column" gap={2}>
          {showProgramName ? (
            <Flex pb={4}>
              <Heading level={2} variant="titleM">
                {program?.program?.name}
              </Heading>
            </Flex>
          ) : null}
          <Box>
            <Text variant="labelL" as="span">
              Intake:
            </Text>{' '}
            <Text variant="bodyM" as="span">
              {program?.programIntakeTerm?.name}
            </Text>
          </Box>

          <Box>
            <Text variant="labelL" as="span">
              Campus:
            </Text>{' '}
            <Text variant="bodyM" as="span">
              {program?.campus?.name ?? '--'}
            </Text>
          </Box>

          <Box>
            <Text variant="labelL" as="span">
              Application ID:
            </Text>{' '}
            <Text variant="bodyM" as="span">
              {application?.attributes?.humanReadableId ?? '--'}
            </Text>
          </Box>

          <Box>
            <Text variant="labelL" as="span">
              Application fee:
            </Text>{' '}
            <Text variant="bodyM" as="span">
              {!application?.attributes?.statusAndCitizenship?.passports
                ? 'To be determined'
                : hasApplicationFee(application)
                  ? Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: application?.attributes?.applicationFee?.currency ?? 'CAD',
                    }).format(applicationFeeAmount)
                  : 'No Fee'}
            </Text>
          </Box>

          {application?.attributes?.applicationInformation?.submittedAt ? (
            <Box>
              <Text variant="labelL" as="span">
                Submission Date:
              </Text>{' '}
              <Text variant="bodyM" as="span">
                {format(
                  new Date(application.attributes.applicationInformation.submittedAt),
                  'MMMM d, yyyy',
                )}
              </Text>
            </Box>
          ) : null}
        </Flex>

        <ReviewRequirementsButton programId={program?.program?.id as string} />

        {alternatePrograms.length > 0
          ? alternatePrograms.map(alternateProgram => {
              const key = crypto.randomUUID()
              return <AlternateProgram key={key} alternateProgram={alternateProgram} />
            })
          : null}
      </Flex>
    </Card>
  )
}

type AlternateProgramProps = Readonly<{
  alternateProgram: AlternateProgramSelected
}>

function AlternateProgram({ alternateProgram }: AlternateProgramProps) {
  return (
    <>
      <Divider m={0} />
      <Flex pt={2} gap={2} direction="column">
        <Text variant="labelL">Alternate Program</Text>
        <Box>
          <Text variant="labelL" as="span">
            Program name:
          </Text>{' '}
          <Text variant="bodyM" as="span">
            {alternateProgram?.program?.name ?? '--'}
          </Text>
        </Box>
        <Box>
          <Text variant="labelL" as="span">
            Intake:
          </Text>{' '}
          <Text variant="bodyM" as="span">
            {alternateProgram?.programIntakeTerm?.name ?? '--'}
          </Text>
        </Box>
        <Box>
          <Text variant="labelL" as="span">
            Campus:
          </Text>{' '}
          <Text variant="bodyM" as="span">
            {alternateProgram?.campus?.name ?? '--'}
          </Text>
        </Box>
      </Flex>
    </>
  )
}
